/**
 * ZAXIA Staff Auth API
 * API client for staff authentication endpoints
 */

import { API_BASE } from '@/config/api';

// =============================================================================
// AUTH TYPES
// =============================================================================

export type AuthRole = 'admin' | 'staff';

export interface StaffUser {
  id: string;
  name: string;
  email: string;
  role: AuthRole;
  createdAt: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  user: StaffUser;
  token: string;
}

export interface AuthError {
  error: string;
  message: string;
}

// =============================================================================
// TOKEN STORAGE
// =============================================================================

const TOKEN_KEY = 'zaxia_staff_token';
const USER_KEY = 'zaxia_staff_user';

/**
 * Store JWT token in localStorage
 */
export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token);
}

/**
 * Get JWT token from localStorage
 */
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/**
 * Remove JWT token from localStorage
 */
export function removeToken(): void {
  localStorage.removeItem(TOKEN_KEY);
}

/**
 * Store user info in localStorage
 */
export function setUser(user: StaffUser): void {
  localStorage.setItem(USER_KEY, JSON.stringify(user));
}

/**
 * Get user info from localStorage
 */
export function getUser(): StaffUser | null {
  const data = localStorage.getItem(USER_KEY);
  if (!data) return null;
  try {
    return JSON.parse(data) as StaffUser;
  } catch {
    return null;
  }
}

/**
 * Remove user info from localStorage
 */
export function removeUser(): void {
  localStorage.removeItem(USER_KEY);
}

/**
 * Clear all auth data
 */
export function clearAuth(): void {
  removeToken();
  removeUser();
}

/**
 * Check if user is authenticated
 */
export function isAuthenticated(): boolean {
  return getToken() !== null;
}

// =============================================================================
// AUTH API
// =============================================================================

/**
 * Login with email and password
 */
export async function login(request: LoginRequest): Promise<LoginResponse> {
  const response = await fetch(`${API_BASE}/auth/login`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(request),
  });

  const data = await response.json();

  if (!response.ok) {
    const error = data as AuthError;
    throw new Error(error.message || 'Login failed');
  }

  const result = data as LoginResponse;

  // Store token and user
  setToken(result.token);
  setUser(result.user);

  return result;
}

/**
 * Get current authenticated user
 */
export async function getCurrentUser(): Promise<StaffUser> {
  const token = getToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const response = await fetch(`${API_BASE}/auth/me`, {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${token}`,
    },
  });

  const data = await response.json();

  if (!response.ok) {
    // Token might be expired
    if (response.status === 401) {
      clearAuth();
    }
    const error = data as AuthError;
    throw new Error(error.message || 'Failed to get user');
  }

  return data.user as StaffUser;
}

/**
 * Logout - clear local auth data
 */
export function logout(): void {
  clearAuth();
}
